import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Meta, Title } from '@angular/platform-browser';
import {
  ActivatedRoute,
  NavigationStart,
  Router,
  RouterModule,
} from '@angular/router';
import { filter, Subscription } from 'rxjs';

import { EventLandingPage } from '../../core/models/event-landing-page.model';
import { AuthService } from '../../core/services/auth.service';
import { ProfileService } from '../../core/services/profile.service';
import { EventRsvpIntentService } from '../../core/services/event-rsvp-intent.service';
import { EventCta, EventViewerState, isTerminalRsvpReason, rsvpReasonMessage, selectEventCta } from '../../core/util/event-rsvp-state.util';
import {
  EventLandingBannerComponent,
  EventLandingCountdown,
} from '../../shared/event-landing-banner/event-landing-banner.component';
import { ShareModalComponent } from '../../search/share-modal/share-modal/share-modal.component';
import { CalendarPickerComponent } from '../../shared/calendar-picker/calendar-picker.component';
import { EventsDTO } from '../../core/models/events.modal';
import { getWebinarStatusError, hasCompleteHostWebinarRoomDetails } from '../../core/util/webinar.util';
import { getAppRsvpUrl } from '../../core/util/event-app-handoff.util';
import { eventDetailsRoute } from '../../core/util/event-route.util';

@Component({
  selector: 'app-event-landing-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    EventLandingBannerComponent,
    ShareModalComponent,
	CalendarPickerComponent,
  ],
  templateUrl: './event-landing-page.component.html',
  styleUrl: './event-landing-page.component.scss',
})
export class EventLandingPageComponent implements OnInit, OnDestroy {
  @ViewChild('shareModal') shareModal?: ShareModalComponent;

  page: EventLandingPage | null = null;
  countdown: EventLandingCountdown | null = null;
  isLoading = true;
  isUnavailable = false;
  viewerState: EventViewerState = {};
  isViewerStateLoading = false;
  isRsvpWorking = false;
  rsvpStatusMessage = '';
  appRsvpUrl: string | null = null;

  private routeSubscription?: Subscription;
  private navigationSubscription?: Subscription;
  private countdownTimer?: ReturnType<typeof setInterval>;
  private statusRefreshTimer?: ReturnType<typeof setInterval>;
  private statusRefreshInFlight = false;
  private loadVersion = 0;
  private canonicalLink?: HTMLLinkElement;
  private canonicalRedirectPage?: EventLandingPage;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private profileService: ProfileService,
    private authService: AuthService,
    private title: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private document: Document,
    private rsvpIntentService: EventRsvpIntentService = new EventRsvpIntentService()
  ) {}

  ngOnInit(): void {
    const routerEvents = (this.router as any).events;
    if (routerEvents) {
      this.navigationSubscription = routerEvents
        .pipe(
          filter(
            (event: unknown): event is NavigationStart =>
              event instanceof NavigationStart
          )
        )
        .subscribe((event: NavigationStart) =>
          this.restoreDefaultMetadata(event.url)
        );
    }
    this.routeSubscription = this.route.paramMap.subscribe((params) => {
      const eventId = Number(params.get('eventId'));
      const suppliedSlug = params.get('slug');
      if (!Number.isSafeInteger(eventId) || eventId <= 0) {
        this.showUnavailable();
        return;
      }
      this.loadLandingPage(eventId, suppliedSlug);
    });
  }

  ngOnDestroy(): void {
    this.appRsvpUrl = null;
    this.loadVersion += 1;
    this.routeSubscription?.unsubscribe();
    this.navigationSubscription?.unsubscribe();
    this.stopCountdown();
    this.stopStatusRefresh();
    this.restoreDefaultMetadata();
  }

  get ctaMode(): 'guest' | 'authenticated' {
    return this.authService.getLogflag() ? 'authenticated' : 'guest';
  }

  get canonicalPath(): string {
    if (!this.page) {
      return '/events';
    }
    return `/events/${this.page.eventId}/${encodeURIComponent(this.page.slug)}`;
  }

  get displayPage(): EventLandingPage | null {
    return this.isViewerStateLoading ? null : this.page;
  }

  get canonicalUrl(): string {
    return `${this.document.location.origin}${this.canonicalPath}`;
  }

	get eventCta(): EventCta {
		return selectEventCta(this.page?.status, this.viewerState, this.authService.getLogflag());
	}

  get isHost(): boolean {
    return Boolean(this.page && this.authService.getLogflag() && this.viewerState.isHost);
  }

	get primaryCtaLabel(): string {
		if (this.isHost) return this.page?.status === 'LIVE' ? 'Rejoin Event' : 'Start Event';
		const labels: Record<EventCta, string> = {
      RSVP: 'RSVP', GOING: 'You’re going', WATCH: 'Watch live', JOIN: 'Join live',
      SIGN_IN_TO_RSVP: 'Sign in to RSVP', SIGN_IN_TO_WATCH: 'Sign in to watch',
      ENDED: 'Event ended', UNAVAILABLE: 'Unavailable', REQUIREMENT: 'View requirement',
		};
		return labels[this.eventCta];
	}

  get showCancelRsvp(): boolean {
		return !this.isHost && this.eventCta === 'GOING';
	}

	get showJoinNotice(): boolean {
		return !this.isHost && this.eventCta === 'JOIN';
	}

  get showSecondaryRsvp(): boolean {
    return !this.isHost && (this.eventCta === 'JOIN' || this.eventCta === 'WATCH') &&
      this.authService.getLogflag() &&
      (Boolean(this.viewerState.isRsvp) || Boolean(this.viewerState.canRsvp));
  }

  get canAddEventToCalendar(): boolean {
    return Boolean(this.page && this.authService.getLogflag() && this.page.status !== 'ENDED'
      && this.page.status !== 'CANCELLED' && (this.viewerState.isRsvp
        || this.isHost));
  }

  get secondaryRsvpLabel(): string {
    return this.viewerState.isRsvp ? 'Cancel RSVP' : 'RSVP';
  }

  get watchNotice(): string {
    return this.eventCta === 'JOIN'
      ? 'Your public name will be visible to other participants.'
      : '';
  }

	onPrimaryAction(): void {
		if (!this.page || this.isRsvpWorking || this.appRsvpUrl) return;
		if (this.isHost) {
      this.onHostPrimaryAction();
      return;
		}
		if (this.eventCta === 'ENDED' || this.eventCta === 'UNAVAILABLE') return;
		if (this.eventCta === 'REQUIREMENT') {
      this.rsvpStatusMessage = rsvpReasonMessage(this.viewerState.watchReason || this.viewerState.rsvpReason);
      return;
		}
		if (this.eventCta === 'SIGN_IN_TO_RSVP') {
      const appUrl = getAppRsvpUrl(this.page.eventId, this.document);
      if (appUrl) this.appRsvpUrl = appUrl;
      else this.continueRsvpInBrowser();
      return;
		}
		if (this.eventCta === 'SIGN_IN_TO_WATCH') {
      void this.router.navigate(['/login'], { queryParams: { returnUrl: this.canonicalPath } });
      return;
		}
		if (this.eventCta === 'JOIN' || this.eventCta === 'WATCH') {
      this.watchEvent();
      return;
		}
		if (this.eventCta === 'GOING') return;
		this.submitRsvp();
	}

  openRsvpInApp(): void {
    if (!this.appRsvpUrl) return;
    try {
      this.document.defaultView?.location.assign(this.appRsvpUrl);
    } catch {
      this.rsvpStatusMessage = 'Could not open CitizeX. Continue in your browser instead.';
    }
  }

  continueRsvpInBrowser(): void {
    if (!this.page) return;
    this.appRsvpUrl = null;
    const eventPath = this.canonicalPath;
    this.rsvpIntentService.clear();
    void this.router.navigate(['/login'], { queryParams: { returnUrl: eventPath } });
  }

	onCancelRsvp(): void {
		if (!this.page || this.isHost || this.isRsvpWorking || !window.confirm('Cancel your RSVP for this event?')) return;
		this.isRsvpWorking = true;
		this.profileService.virtualEventCancelRsvp(new HttpParams().set('eventId', String(this.page.eventId))).subscribe({
      next: (response: any) => {
			this.isRsvpWorking = false;
			if (response?.success === 0) {
        this.applyViewerState(response.data);
        this.rsvpStatusMessage = 'Your RSVP has been cancelled. Calendar entries you added are not removed automatically.';
			} else this.rsvpStatusMessage = rsvpReasonMessage(response?.message);
      },
      error: (error: any) => { this.isRsvpWorking = false; this.rsvpStatusMessage = rsvpReasonMessage(error?.error?.message); },
		});
	}

  onSecondaryRsvp(): void {
    if (this.isHost) return;
    if (this.viewerState.isRsvp) this.onCancelRsvp();
    else this.submitRsvp();
  }

  openShareModal(): void {
    this.shareModal?.openModal({ currentUrl: this.canonicalUrl });
  }

  private onHostPrimaryAction(): void {
    if (!this.page) return;
    const user = this.authService.getUser();
    if (!user?.verified) {
      this.rsvpStatusMessage = 'Verify your account before starting or rejoining this event.';
      return;
    }
    const eventId = this.page.eventId;
    this.isRsvpWorking = true;
    this.rsvpStatusMessage = '';
    this.profileService.fetchVirtualEventById(new HttpParams().set('eventId', String(eventId))).subscribe({
      next: (response: any) => {
        if (response?.success !== 0 || response?.data?.id !== eventId) {
          this.isRsvpWorking = false;
          this.rsvpStatusMessage = getWebinarStatusError(response, 'Unable to load this event. Please try again.');
          return;
        }
        const event = EventsDTO.fromResponse(response.data);
        const status = event.eventStatus?.trim().toLowerCase();
        if (['closed', 'ended', 'end', 'stop', 'stopped', 'cancelled', 'canceled', 'unpublished'].includes(status)) {
          this.isRsvpWorking = false;
          this.rsvpStatusMessage = 'This event is no longer available.';
          this.refreshLandingStatus();
          return;
        }
        if (!hasCompleteHostWebinarRoomDetails(event)) {
          this.isRsvpWorking = false;
          this.rsvpStatusMessage = 'This event is missing webinar access details. Open event details to repair it.';
          return;
        }
        const openHostRoom = () => {
          this.isRsvpWorking = false;
          void this.router.navigate(['/profile/events/', event.webinarId], {
            queryParams: { isHost: true, openChatRoom: true },
          });
        };
        if (['live', 'start', 'started'].includes(status)) {
          openHostRoom();
          return;
        }
        this.profileService.updateWebinarStatus(new HttpParams()
          .set('eventId', String(eventId))
          .set('webinarStatus', 'START')).subscribe({
            next: (result: any) => {
              if (result?.success === 0) openHostRoom();
              else {
                this.isRsvpWorking = false;
                this.rsvpStatusMessage = getWebinarStatusError(result);
              }
            },
            error: (error: any) => {
              this.isRsvpWorking = false;
              this.rsvpStatusMessage = getWebinarStatusError(error);
            },
          });
      },
      error: (error: any) => {
        this.isRsvpWorking = false;
        this.rsvpStatusMessage = getWebinarStatusError(error, 'Unable to load this event. Please try again.');
      },
    });
  }

  private loadLandingPage(eventId: number, suppliedSlug: string | null): void {
    const requestVersion = ++this.loadVersion;
    this.isLoading = true;
    this.isViewerStateLoading = false;
    this.rsvpStatusMessage = '';
    this.isUnavailable = false;
    this.page = null;
    this.countdown = null;
    this.stopCountdown();
    this.stopStatusRefresh();

    const redirectedPage = this.canonicalRedirectPage;
    if (
      redirectedPage &&
      redirectedPage.eventId === eventId &&
      redirectedPage.slug === suppliedSlug
    ) {
      this.canonicalRedirectPage = undefined;
      this.setPage(redirectedPage);
      return;
    }
    this.canonicalRedirectPage = undefined;

    this.profileService.fetchPublicEventLandingPage(eventId).subscribe({
      next: (response: any) => {
        if (requestVersion !== this.loadVersion) return;
        if (response?.success === 0 && !response?.data) {
          this.redirectToEventDetails(eventId, requestVersion);
          return;
        }
        if (response?.success !== 0 || !response?.data) {
          this.showUnavailable();
          return;
        }

        const page = response.data as EventLandingPage;
        if (!page.slug || !page.hostTag) {
          this.showUnavailable();
          return;
        }

        const canonicalPath = `/events/${page.eventId}/${encodeURIComponent(
          page.slug
        )}`;
        if (suppliedSlug !== page.slug) {
          this.canonicalRedirectPage = page;
          void this.router
            .navigateByUrl(canonicalPath, { replaceUrl: true })
            .then((navigated) => {
              // Router activation normally emits the canonical parameter map
              // before this promise resolves. The fallback covers a cancelled
              // or mocked navigation without issuing a second public request.
              if (navigated && requestVersion === this.loadVersion) {
                this.canonicalRedirectPage = undefined;
                this.setPage(page);
              }
            });
          return;
        }

        this.setPage(page);
      },
      error: () => {
        if (requestVersion === this.loadVersion) {
          this.showUnavailable();
        }
      },
    });
  }

  private redirectToEventDetails(eventId: number, requestVersion: number): void {
    this.profileService.fetchVirtualEventByIdWithoutGlobalLoader(new HttpParams().set('eventId', String(eventId))).subscribe({
      next: (response: any) => {
        if (requestVersion !== this.loadVersion) return;
        const route = response?.success === 0 && response?.data?.id === eventId
          ? eventDetailsRoute(response.data)
          : null;
        if (route) {
          void this.router.navigate(route, { replaceUrl: true });
        } else {
          this.showUnavailable();
        }
      },
      error: () => {
        if (requestVersion === this.loadVersion) this.showUnavailable();
      },
    });
  }

  private setPage(page: EventLandingPage): void {
    this.stopCountdown();
    this.stopStatusRefresh();
    const existingViewerState = this.page?.eventId === page.eventId ? this.viewerState : null;
    this.page = page;
    if (existingViewerState && !this.isViewerStateLoading) {
      this.viewerState = existingViewerState;
    } else {
      this.applyViewerState(page.viewingState || {});
      this.isViewerStateLoading = this.authService.getLogflag();
    }
    this.isLoading = false;
    this.isUnavailable = false;
    this.updateCountdown();
    if (!this.page || this.isUnavailable) {
      return;
    }
    this.applyLandingMetadata(this.page);
		this.refreshViewerState();
    if (this.page.status === 'UPCOMING') {
      if (this.countdown) {
        this.countdownTimer = setInterval(() => this.updateCountdown(), 1000);
      }
      this.startStatusRefresh();
    } else if (this.page.status === 'LIVE') {
      this.startStatusRefresh();
    }
  }

	private refreshViewerState(): void {
		if (!this.page || !this.authService.getLogflag() || typeof this.profileService.fetchVirtualEventWatchState !== 'function') return;
		const eventId = this.page.eventId;
		const requestVersion = this.loadVersion;
		this.profileService.fetchVirtualEventWatchState(eventId).subscribe({
      next: (response: any) => {
            if (requestVersion !== this.loadVersion || this.page?.eventId !== eventId) return;
			if (response?.success !== 0 || !response.data) {
              this.rsvpStatusMessage = 'Unable to verify event access. Reload the page to try again.';
              return;
            }
			this.applyViewerState(response.data);
            this.isViewerStateLoading = false;
			// A returned guest should see the event and use its RSVP button.
			// Discard an intent left by older versions of this handoff.
			if (this.rsvpIntentService.matchesFor(this.page.eventId, this.canonicalPath)) {
        this.rsvpIntentService.clear();
			}
      },
      error: () => {
            if (requestVersion !== this.loadVersion || this.page?.eventId !== eventId) return;
            this.rsvpStatusMessage = 'Unable to verify event access. Reload the page to try again.';
          },
		});
	}

	private applyViewerState(data: EventViewerState): void {
		this.viewerState = {
      isRsvp: Boolean(data?.isRsvp),
      isHost: data?.isHost,
      canRsvp: Boolean(data?.canRsvp),
      rsvpReason: data?.rsvpReason || null,
      canWatch: Boolean(data?.canWatch),
      watchMode: data?.watchMode || null,
      watchReason: data?.watchReason || null,
      chatReason: data?.chatReason || null,
      rsvpCount: data?.rsvpCount,
		};
	}

	private submitRsvp(): void {
		if (!this.page || this.isHost) return;
		this.isRsvpWorking = true;
		this.rsvpStatusMessage = '';
		this.profileService.virtualEventRsvp(new HttpParams().set('eventId', String(this.page.eventId))).subscribe({
      next: (response: any) => {
        this.isRsvpWorking = false;
        if (response?.success !== 0) {
          if (isTerminalRsvpReason(response?.message)) this.rsvpIntentService.clear();
          this.rsvpStatusMessage = rsvpReasonMessage(response?.message);
        return;
			}
			this.rsvpIntentService.clear();
			this.applyViewerState(response.data);
			this.rsvpStatusMessage = '';
      },
      error: (error: any) => {
				this.isRsvpWorking = false;
				const reason = error?.error?.message;
				if (isTerminalRsvpReason(reason)) this.rsvpIntentService.clear();
				this.rsvpStatusMessage = rsvpReasonMessage(reason);
      },
		});
	}

  private watchEvent(): void {
    if (!this.page) return;
    // EventComponent owns the authorized Watch handoff and embeds the provider.
    // Keeping this navigation first-party preserves the CitizeX event shell.
    void this.router.navigate(['/profile/events/', this.page.eventId]);
  }

  private updateCountdown(): void {
    if (!this.page) {
      this.countdown = null;
      return;
    }

    const now = Date.now();
    const startsAt = new Date(this.page.startsAt).getTime();
    if (this.page.status !== 'UPCOMING') {
      this.countdown = null;
      this.stopCountdown();
      return;
    }

    if (now >= startsAt) {
      this.countdown = null;
      this.stopCountdown();
      return;
    }

    const remaining = startsAt - now;
    if (!Number.isFinite(remaining) || remaining <= 0) {
      this.countdown = null;
      this.stopCountdown();
      return;
    }

    this.countdown = {
      days: Math.floor(remaining / 86_400_000),
      hours: Math.floor((remaining % 86_400_000) / 3_600_000),
      minutes: Math.floor((remaining % 3_600_000) / 60_000),
      seconds: Math.floor((remaining % 60_000) / 1000),
    };
  }

  private showUnavailable(): void {
    this.stopCountdown();
    this.stopStatusRefresh();
    this.page = null;
    this.countdown = null;
    this.isLoading = false;
    this.isUnavailable = true;
    this.restoreDefaultMetadata();
  }

  private stopCountdown(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = undefined;
    }
  }

  private startStatusRefresh(): void {
    this.statusRefreshTimer = setInterval(
      () => this.refreshLandingStatus(),
      30_000
    );
  }

  private stopStatusRefresh(): void {
    if (this.statusRefreshTimer) {
      clearInterval(this.statusRefreshTimer);
      this.statusRefreshTimer = undefined;
    }
    this.statusRefreshInFlight = false;
  }

  private refreshLandingStatus(): void {
    const currentPage = this.page;
    if (
      !currentPage ||
      this.statusRefreshInFlight ||
      (currentPage.status !== 'UPCOMING' && currentPage.status !== 'LIVE')
    ) {
      return;
    }

    const requestVersion = this.loadVersion;
    this.statusRefreshInFlight = true;
    this.profileService
      .fetchPublicEventLandingPage(currentPage.eventId)
      .subscribe({
        next: (response: any) => {
          this.statusRefreshInFlight = false;
          if (
            requestVersion !== this.loadVersion ||
            response?.success !== 0 ||
            !response?.data ||
            response.data.eventId !== currentPage.eventId
          ) {
            return;
          }
          this.setPage(response.data as EventLandingPage);
        },
        error: () => {
          this.statusRefreshInFlight = false;
        },
      });
  }

  private applyLandingMetadata(page: EventLandingPage): void {
    const canonicalUrl = `${this.document.location.origin}/events/${
      page.eventId
    }/${encodeURIComponent(page.slug)}`;
    const lifecycleLabel =
      page.status === 'ENDED'
        ? 'Event ended'
        : page.status === 'CANCELLED'
        ? 'Event cancelled'
        : '';
    const title = `${page.heading}${
      lifecycleLabel ? ` — ${lifecycleLabel}` : ''
    } | CitizeX`;
    const description = this.truncateMetadataDescription(
      [
        lifecycleLabel ? `${lifecycleLabel}.` : '',
        page.description || 'View this event on CitizeX.',
      ]
        .filter(Boolean)
        .join(' ')
    );
    const image =
      page.imageUrl ||
      `${this.document.location.origin}/assets/images/logo-text.png`;
    const imageAlt = page.imageUrl ? page.imageAlt || page.heading : 'CitizeX';

    this.title.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({
      property: 'og:title',
      content: title,
    });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: canonicalUrl });
    this.meta.updateTag({ property: 'og:image', content: image });
    this.meta.updateTag({ property: 'og:image:alt', content: imageAlt });
    this.meta.updateTag({
      name: 'twitter:card',
      content: page.imageUrl ? 'summary_large_image' : 'summary',
    });
    this.meta.updateTag({
      name: 'twitter:title',
      content: title,
    });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: image });
    this.meta.updateTag({ name: 'twitter:image:alt', content: imageAlt });

    this.setCanonicalUrl(canonicalUrl);
  }

  private truncateMetadataDescription(value: string): string {
    const normalized = value.replace(/\s+/g, ' ').trim();
    return normalized.length > 240
      ? `${normalized.slice(0, 239).trimEnd()}…`
      : normalized;
  }

  private restoreDefaultMetadata(nextPath?: string): void {
    const currentPath =
      nextPath || this.router.url || this.document.location.pathname || '/';
    const canonicalUrl = `${this.document.location.origin}${currentPath}`;
    const description = 'Join the conversation on CitizeX.';
    const image = `${this.document.location.origin}/assets/images/logo-text.png`;

    this.title.setTitle('CitizeX');
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:title', content: 'CitizeX' });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: canonicalUrl });
    this.meta.updateTag({ property: 'og:image', content: image });
    this.meta.updateTag({ property: 'og:image:alt', content: 'CitizeX' });
    this.meta.updateTag({ name: 'twitter:card', content: 'summary' });
    this.meta.updateTag({ name: 'twitter:title', content: 'CitizeX' });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: image });
    this.meta.updateTag({ name: 'twitter:image:alt', content: 'CitizeX' });
    this.setCanonicalUrl(canonicalUrl);
  }

  private setCanonicalUrl(canonicalUrl: string): void {
    this.canonicalLink =
      this.document.querySelector('link[rel="canonical"]') || undefined;
    if (!this.canonicalLink) {
      this.canonicalLink = this.document.createElement('link');
      this.canonicalLink.rel = 'canonical';
      this.document.head.appendChild(this.canonicalLink);
    }
    this.canonicalLink.href = canonicalUrl;
  }
}
